import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@fluentui/react-components';
import { useSinglePlacementLayouts } from '@bingads-webui-campaign-react/ad-placement-factory';
import { AdPlacementsGroupType } from '@bingads-webui-campaign-react/ad-placement-consts';

const useStyles = makeStyles({
  adPreviewContainer: {
    '& .new-responsive-ad-preview-container': {
      opacity: '1 !important',
    },
    '& .cropped-image-view': {
      height: 'auto !important',
    },
    '& .cropped-image-view > img': {
      width: '286px',
    },
  },
});

const createCenterCroppedImage = (url, width, height) => {
  const aspectRatio = 1.91;
  const aspectRatioAssociationType = 'LandscapeImageMedia';
  const orgAspectRatio = width / height;

  let cropHeight = height;
  let cropWidth = width;

  if (aspectRatio > orgAspectRatio) {
    cropHeight = cropWidth / aspectRatio;
  } else {
    cropWidth = cropHeight * aspectRatio;
  }

  return {
    Src: url,
    FinalSrc: url,
    Width: width,
    Height: height,
    CropSettings: {
      SourceX: (width - cropWidth) / 2,
      SourceY: (height - cropHeight) / 2,
      SourceWidth: cropWidth,
      SourceHeight: cropHeight,
    },
    associationType: aspectRatioAssociationType,
  };
};

const AdPreviewComponent = (props) => {
  const {
    image,
    adData,
    adId,
  } = props;
  const classes = useStyles();

  const safeAdData = useMemo(() => {
    if (!adData) {
      return adData;
    }
    // The placement renderer calls String.prototype.split on these text fields.
    // The API can return null for empty ads (e.g. null Title/Description), which
    // would throw "Cannot read properties of null (reading 'split')", so coerce
    // nullish values to empty strings.
    return {
      ...adData,
      shortHeadline: adData.shortHeadline || '',
      longHeadline: adData.longHeadline || '',
      description: adData.description || '',
      displayUrl: adData.displayUrl || '',
      url: adData.url || '',
    };
  }, [adData]);

  const imageList = useMemo(() => {
    if (!image) return [];
    return [createCenterCroppedImage(image.url, image.width, image.height)];
  }, [image]);

  const placementImageFilterPredicate = useCallback((template) => {
    const { associationType } = template;
    return associationType === 'LandscapeImageMedia';
  }, []);

  const placementTextFilterPredicate = useCallback((template) => {
    const { associationType } = template;
    return associationType === 'BingText';
  }, []);

  const [{ SinglePlacementLayout }] = useSinglePlacementLayouts({
    groupType: AdPlacementsGroupType.adLibrary,
    placementFilterPredicate: !image ? placementTextFilterPredicate : placementImageFilterPredicate,
  });

  return (
    <div className={classes.adPreviewContainer}>
      <SinglePlacementLayout key={adId} adData={safeAdData} images={imageList} />
    </div>
  );
};

AdPreviewComponent.propTypes = {
  image: PropTypes.shape({
    url: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  adData: PropTypes.shape({
    shortHeadline: PropTypes.string,
    longHeadline: PropTypes.string,
    description: PropTypes.string,
    displayUrl: PropTypes.string,
    url: PropTypes.string,
  }),
  adId: PropTypes.string.isRequired,
};

AdPreviewComponent.defaultProps = {
  adData: null,
  image: null,
};

export const AdPreview = AdPreviewComponent;
